import { Capacitor } from '@capacitor/core';
import { stripTrailingSlash } from '@up2stay/up2promo-commons/auth';

import { env } from '@/core/env';

const DEFAULT_API_PORT = '3000';

/** Dev server on a LAN IP / Capacitor live reload: never call localhost from a device. */
const DEV_REMOTE_API_FALLBACK = 'https://api.dev.up2promo.com';

/** True if hostname is a private/network IP or localhost (not reachable from other devices or when backend isn't on that host). */
function isPrivateOrLocalHost(hostname: string): boolean {
  return (
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname.startsWith('192.168.') ||
    hostname.startsWith('10.') ||
    /^172\.(1[6-9]|2[0-9]|3[0-1])\./.test(hostname)
  );
}

/** True if hostname is a private/network IP (excluding localhost). */
function isPrivateNetworkHost(hostname: string): boolean {
  return (
    hostname.startsWith('192.168.') ||
    hostname.startsWith('10.') ||
    /^172\.(1[6-9]|2[0-9]|3[0-1])\./.test(hostname)
  );
}

/** Dev server only: VITE_API_URL may be unset here, which the typed `env` rejects. */
function readDevEnv(key: 'VITE_API_URL' | 'VITE_API_PORT'): string {
  const value: unknown = import.meta.env[key];
  return typeof value === 'string' ? value.trim() : '';
}

/**
 * Effective API base URL. A production build (web or native) uses VITE_API_URL
 * as-is: the build rejects a missing value, so there is no fallback.
 */
function getApiBaseUrl(): string {
  if (!import.meta.env.DEV) {
    return stripTrailingSlash(env.VITE_API_URL);
  }
  return getDevApiBaseUrl();
}

/**
 * Dev server only: env first, then on device use same host as page with API port.
 * When opened from a network IP (e.g. 192.168.x.x) without a valid VITE_API_URL we use
 * the dev API so API/images work instead of connection refused.
 * A localhost VITE_API_URL / VITE_API_PORT only counts when the page itself is
 * served from localhost (vite dev), so a second local API (e.g. :3100) can be targeted.
 * On Capacitor live reload we never return localhost (device cannot reach it).
 */
function getDevApiBaseUrl(): string {
  const trimmed = readDevEnv('VITE_API_URL');

  // 1) Prefer VITE_API_URL when it's set and points to a real backend (https or non-local)
  if (
    trimmed &&
    (trimmed.toLowerCase().startsWith('https') ||
      (!trimmed.includes('localhost') && !trimmed.includes('127.0.0.1')))
  ) {
    return trimmed;
  }

  if (
    typeof window !== 'undefined' &&
    window.location?.origin?.startsWith('http')
  ) {
    const hostname = window.location.hostname;
    const protocol = window.location.protocol;
    if (hostname && hostname !== 'localhost' && hostname !== '127.0.0.1') {
      // 2) When opened from a private IP (e.g. 192.168.178.135:3003), avoid using hostname:3000
      //    unless the backend is really there – use production so API/images work
      if (isPrivateNetworkHost(hostname)) {
        return DEV_REMOTE_API_FALLBACK;
      }
      const port = readDevEnv('VITE_API_PORT') || DEFAULT_API_PORT;
      return `${protocol}//${hostname}:${port}`;
    }
    // 3) Page on localhost (vite dev): honour an explicit local VITE_API_URL,
    //    then VITE_API_PORT. Android Cap also serves from localhost, so skip native.
    if (!Capacitor.isNativePlatform()) {
      if (trimmed) return trimmed;
      const port = readDevEnv('VITE_API_PORT');
      if (port) return `http://localhost:${port}`;
    }
  }

  // 4) Capacitor native (e.g. capacitor://localhost): never use localhost
  if (typeof window !== 'undefined' && Capacitor.isNativePlatform()) {
    return DEV_REMOTE_API_FALLBACK;
  }

  return `http://localhost:${DEFAULT_API_PORT}`;
}

export { getApiBaseUrl };

/**
 * Returns an absolute URL for a file path from the API.
 * Use for img src, a href, etc., so images load on iPhone/Capacitor and in
 * browsers when the page is served from a different origin than the API.
 * If the API returns a full URL with a private/local host (e.g. 192.168.x.x),
 * we rewrite it to the current API base so images load correctly.
 */
/** Static files from the app bundle (Vite `public/`), not the API. */
export const PREVIEW_SAMPLE_AVATAR_PATH =
  '/assets/images/preview-sample-avatar.svg';

export function resolveAppAssetFileUrl(path: string): string {
  const trimmed = path.trim();
  if (
    trimmed.startsWith('/assets/') &&
    typeof window !== 'undefined' &&
    window.location?.origin
  ) {
    return `${window.location.origin}${trimmed}`;
  }
  return getAbsoluteFileUrl(trimmed);
}

const LOCAL_FILES_PREFIX = '/api/v1/files/';

/**
 * Stable storage key for a file path returned by the API.
 * Strips presigned query strings and `/api/v1/files/` prefixes so module
 * configs never persist `?X-Amz-…` URLs (DEV-458).
 */
export function toStoredFileKey(path: string | null | undefined): string {
  const trimmed = (path ?? '').trim();
  if (!trimmed) return trimmed;

  if (!trimmed.startsWith('http://') && !trimmed.startsWith('https://')) {
    if (trimmed.includes(LOCAL_FILES_PREFIX)) {
      return trimmed.slice(
        trimmed.indexOf(LOCAL_FILES_PREFIX) + LOCAL_FILES_PREFIX.length,
      );
    }
    return trimmed;
  }

  try {
    const url = new URL(trimmed);
    if (
      url.hostname.includes('amazonaws.com') ||
      url.hostname.includes('.s3.')
    ) {
      return decodeURIComponent(url.pathname.replace(/^\//, ''));
    }
    if (url.pathname.includes(LOCAL_FILES_PREFIX)) {
      return url.pathname.slice(
        url.pathname.indexOf(LOCAL_FILES_PREFIX) + LOCAL_FILES_PREFIX.length,
      );
    }
    return url.pathname.startsWith('/') ? url.pathname : `/${url.pathname}`;
  } catch {
    return trimmed;
  }
}

export function getAbsoluteFileUrl(path: string | null | undefined): string {
  if (path == null || path === '') return '';
  // data:/blob: must stay as-is — never prefix with the API origin (causes 431).
  if (
    path.startsWith('data:') ||
    path.startsWith('blob:') ||
    path.startsWith('http://') ||
    path.startsWith('https://')
  ) {
    if (path.startsWith('data:') || path.startsWith('blob:')) {
      return path;
    }
    try {
      const url = new URL(path);
      if (isPrivateOrLocalHost(url.hostname)) {
        const base = getApiBaseUrl().replace(/\/$/, '');
        return (
          base +
          (url.pathname.startsWith('/') ? url.pathname : `/${url.pathname}`)
        );
      }
      return path;
    } catch {
      return path;
    }
  }
  const base = getApiBaseUrl().replace(/\/$/, '');
  return base + (path.startsWith('/') ? path : `/${path}`);
}
